import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { TenantContextService } from "../services/tenant-context.service";

/**
 * 2026-05-06 H-1 — Operations route guard. Allows the three operations
 * roles (super_admin / tenant_admin / tenant_manager) per the role
 * ownership matrix in `docs/plans/2026-05-06-master-phased-plan.md` §1.
 *
 * Use this guard for routes that need to be reachable by tenant_manager
 * but NOT by supervisor / teacher / student / parent. Examples:
 *   - money-transfer-requests review
 *   - edit-data-requests review
 *   - announcements composer
 *   - evaluations
 *   - student/teacher pipeline operations
 *
 * If a route also requires tenant-level governance (pricing, branding,
 * role mgmt), use TenantAdminGuard instead — that's a stricter set.
 *
 * Same race-handling pattern as SuperAdminGuard / TenantAdminGuard:
 * await TenantContextService.load() if memberships haven't resolved.
 */
export const OperationsGuard: CanActivateFn = async () => {
  const router = inject(Router);
  const tenantContext = inject(TenantContextService);

  if (tenantContext.memberships().length === 0 && !tenantContext.active()) {
    try {
      await tenantContext.load();
    } catch {
      /* fall through to deny below */
    }
  }

  if (tenantContext.canManageOperations()) return true;

  router.navigateByUrl("/home");
  return false;
};
